import React from "react";
import ReportTable from "../ReportTable";
import MultiHeaderReportTable from "../MultiHeaderReportTable";
import { principleTitles, renderYesNo, safeText } from "./helpers";
import usePrincipleData from "./usePrincipleData";
import useReportingYearLabels from "./useReportingYearLabels";

const Principle3 = ({ styles }) => {
  const { loading, principle } = usePrincipleData(3);
  const { currentYear, previousYear } = useReportingYearLabels();
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};

  const fyLineA = currentYear
    ? `a. In FY ${currentYear} (Current Financial Year)`
    : "a. In Current Financial Year (N/A)";
  const fyLineB = previousYear
    ? `b. In FY ${previousYear} (Previous Financial Year)`
    : "b. In Previous Financial Year (N/A)";
  const fyInCurrent = currentYear
    ? `In FY ${currentYear} (Current Financial Year)`
    : "In Current Financial Year (N/A)";
  const fyInPrevious = previousYear
    ? `In FY ${previousYear} (Previous Financial Year)`
    : "In Previous Financial Year (N/A)";

  const fyCurrentColLabel = currentYear
    ? `In FY ${currentYear} (Current Financial Year)`
    : "Current Financial Year (N/A)";
  const fyPreviousColLabel = previousYear
    ? `In FY ${previousYear} (Previous Financial Year)`
    : "Previous Financial Year (N/A)";

  const pickFirst = (...values) => values.find((v) => v !== undefined && v !== null && v !== "");

  const buildWellBeingBenefitRows = (section, idPrefix) => {
    const benefits = [
      ["Health Insurance", "healthInsurance"],
      ["Accident Insurance", "accidentInsurance"],
      ["Maternity Benefits", "maternityBenefits"],
      ["Paternity Benefits", "paternityBenefits"],
      ["Day Care Facilities", "dayCareFacilities"],
      ["Total", "Total"],
    ];

    return benefits.map(([coveredBy, key], idx) => ({
      _id: `${idPrefix}-${idx}`,
      coveredBy,
      maleNum: safeText(section?.[key]?.male, "NA"),
      malePct: safeText(section?.[key]?.malePercentage, "N/A"),
      femaleNum: safeText(section?.[key]?.female, "NA"),
      femalePct: safeText(section?.[key]?.femalePercentage, "N/A"),
    }));
  };
  console.log(essential, "employeePermanentBenefitRows");
  const employeeTotals = {
    malePermanent: safeText(
      essential?.wellBeingMeasures?.maleEmployees?.permanentEmployees,
      "N/A",
    ),
    maleOther: safeText(
      essential?.wellBeingMeasures?.maleEmployees?.otherEmployees,
      "N/A",
    ),
    femalePermanent: safeText(
      essential?.wellBeingMeasures?.femaleEmployees?.permanentEmployees,
      "N/A",
    ),
    femaleOther: safeText(
      essential?.wellBeingMeasures?.femaleEmployees?.otherEmployees,
      "N/A",
    ),
  };

  const workerTotals = {
    malePermanent: safeText(
      essential?.wellBeingWorkers?.maleEmployees?.permanentWorkers,
      "N/A",
    ),
    maleOther: safeText(
      essential?.wellBeingWorkers?.maleEmployees?.otherWorkers,
      "N/A",
    ),
    femalePermanent: safeText(
      essential?.wellBeingWorkers?.femaleEmployees?.permanentWorkers,
      "N/A",
    ),
    femaleOther: safeText(
      essential?.wellBeingWorkers?.femaleEmployees?.otherWorkers,
      "N/A",
    ),
  };

  const employeePermanentBenefitRows = buildWellBeingBenefitRows(
    essential?.wellBeingMeasures?.permanentEmployees,
    "p3-e1-emp-perm",
  );
  const employeeOtherBenefitRows = buildWellBeingBenefitRows(
    essential?.wellBeingMeasures?.otherEmployees,
    "p3-e1-emp-other",
  );
  const workerPermanentBenefitRows = buildWellBeingBenefitRows(
    essential?.wellBeingWorkers?.permanentWorkers,
    "p3-e1-work-perm",
  );
  const workerOtherBenefitRows = buildWellBeingBenefitRows(
    essential?.wellBeingWorkers?.otherWorkers,
    "p3-e1-work-other",
  );

  const retirementRows = [
    ["PF", "pf"],
    ["Gratuity", "gratuity"],
    ["ESI", "ESI"],
    [
      safeText(
        essential?.retirementBenefits?.currentFinancialYear?.others
          ?.headerOthers,
        "Others - Please Specify",
      ),
      "others",
    ],
  ].map(([benefit, key], idx) => {
    const current = essential?.retirementBenefits?.currentFinancialYear?.[key];
    const previous =
      essential?.retirementBenefits?.previousFinancialYear?.[key];
    return {
      _id: `p3-e2-${idx}`,
      benefit,
      currentEmployees: safeText(current?.employeesCovered, "N/A"),
      currentWorkers: safeText(current?.workersCovered, "N/A"),
      currentDeducted: safeText(current?.deductedDeposited, "N/A"),
      previousEmployees: safeText(previous?.employeesCovered, "N/A"),
      previousWorkers: safeText(previous?.workersCovered, "N/A"),
      previousDeducted: safeText(previous?.deductedDeposited, "N/A"),
    };
  });

  const employeeReturnRetentionRows = [
    ["Male", "male"],
    ["Female", "female"],
    ["Total", "total"],
  ].map(([gender, key], idx) => ({
    _id: `p3-e5-emp-${idx}`,
    gender,
    returnToWorkRate: safeText(
      essential?.returnAndRetentionParentalLeave?.permanentEmployees?.[key]
        ?.returnToWorkRate,
      "N/A",
    ),
    retentionRate: safeText(
      essential?.returnAndRetentionParentalLeave?.permanentEmployees?.[key]
        ?.retentionRate,
      "N/A",
    ),
  }));

  const workerReturnRetentionRows = [
    ["Male", "male"],
    ["Female", "female"],
    ["Total", "total"],
  ].map(([gender, key], idx) => ({
    _id: `p3-e5-work-${idx}`,
    gender,
    returnToWorkRate: safeText(
      essential?.returnAndRetentionParentalLeave?.permanentWorkers?.[key]
        ?.returnToWorkRate,
      "N/A",
    ),
    retentionRate: safeText(
      essential?.returnAndRetentionParentalLeave?.permanentWorkers?.[key]
        ?.retentionRate,
      "N/A",
    ),
  }));

  const grievanceRows = [
    ["Permanent Employees", "permanentEmployees"],
    ["Other than Permanent Employees", "otherThanPermanentEmployees"],
    ["Permanent Workers", "permanentWorkers"],
    ["Other than Permanent Workers", "otherThanPermanentWorkers"],
  ].map(([segment, key], idx) => ({
    _id: `p3-g-${idx}`,
    segment,
    mechanism: safeText(
      essential?.grievanceRedressalMechanism?.[key]?.code,
      "N/A",
    ),
    details: safeText(
      essential?.grievanceRedressalMechanism?.[key]?.remark,
      "N/A",
    ),
  }));

  const membershipRowsFor = (block, idPrefix) => {
    const male = block?.totalMaleEmployees;
    const female = block?.totalFemaleEmployees;
    const total = block?.totalEmployeesCategory;
    const unionTotal = block?.NoofEmployeesCategory;
    const unionMale = block?.NoOfMaleEmployeesCategory;
    const unionFemale = block?.NoOfFemaleEmployeesCategory;
    const pctMale = block?.totalMaleEmployeesPercentage;
    const pctFemale = block?.totalfemaleEmployeesPercentage;
    const pctTotal = safeText(
      block?.totalEmployeesCategory && block?.NoofEmployeesCategory
        ? (
            (Number(block.NoofEmployeesCategory) /
              Number(block.totalEmployeesCategory)) *
            100
          ).toFixed(2)
        : "",
      "N/A",
    );

    return [
      {
        _id: `${idPrefix}-m`,
        category: "Male",
        total: safeText(male, "N/A"),
        inUnion: safeText(unionMale, "N/A"),
        percentage: safeText(pctMale, "N/A"),
      },
      {
        _id: `${idPrefix}-f`,
        category: "Female",
        total: safeText(female, "N/A"),
        inUnion: safeText(unionFemale, "N/A"),
        percentage: safeText(pctFemale, "N/A"),
      },
      {
        _id: `${idPrefix}-t`,
        category: "Total",
        total: safeText(total, "N/A"),
        inUnion: safeText(unionTotal, "N/A"),
        percentage: pctTotal,
      },
    ];
  };

  const trainingRowsFor = (block, idPrefix) => [
    {
      _id: `${idPrefix}-m`,
      category: "Male",
      total: safeText(block?.totalMale, "N/A"),
      hsNumber: safeText(block?.HealthMaleNumber, "N/A"),
      hsPercent: safeText(block?.HealthMalePercentage, "N/A"),
      skillNumber: safeText(block?.SkillMaleNumber, "N/A"),
      skillPercent: safeText(block?.SkillMalePercentage, "N/A"),
    },
    {
      _id: `${idPrefix}-f`,
      category: "Female",
      total: safeText(block?.totalFemale, "N/A"),
      hsNumber: safeText(block?.HealthFemaleNumber, "N/A"),
      hsPercent: safeText(block?.HealthFemalePercentage, "N/A"),
      skillNumber: safeText(block?.SkillFemaleNumber, "N/A"),
      skillPercent: safeText(block?.SkillFemalePercentage, "N/A"),
    },
    {
      _id: `${idPrefix}-t`,
      category: "Total",
      total: safeText(block?.Total, "N/A"),
      hsNumber: safeText(block?.HealthTotal, "N/A"),
      hsPercent: "-",
      skillNumber: safeText(block?.SkillTotal, "N/A"),
      skillPercent: "-",
    },
  ];

  const performanceRowsFor = (block, idPrefix) => [
    {
      _id: `${idPrefix}-m`,
      category: "Male",
      total: safeText(block?.totalEmployee?.male, "N/A"),
      reviewed: safeText(block?.employee?.male, "N/A"),
      percentage: safeText(block?.Percentage?.male, "N/A"),
    },
    {
      _id: `${idPrefix}-f`,
      category: "Female",
      total: safeText(block?.totalEmployee?.female, "N/A"),
      reviewed: safeText(block?.employee?.female, "N/A"),
      percentage: safeText(block?.Percentage?.female, "N/A"),
    },
    {
      _id: `${idPrefix}-t`,
      category: "Total",
      total: safeText(block?.totalEmployee?.total, "N/A"),
      reviewed: safeText(block?.employee?.total, "N/A"),
      percentage: safeText(block?.Percentage?.total, "N/A"),
    },
  ];

  const safetyIncidentRowsFor = (block, idPrefix) => [
    {
      _id: `${idPrefix}-ltifr`,
      incident: "Lost Time Injury Frequency Rate (LTIFR)",
      current: safeText(
        block?.lostTimeInjuryFrequencyRate?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        block?.lostTimeInjuryFrequencyRate?.previousFinancialYear,
        "N/A",
      ),
    },
    {
      _id: `${idPrefix}-tri`,
      incident: "Total Recordable injuries",
      current: safeText(
        block?.totalRecordableInjuries?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        block?.totalRecordableInjuries?.previousFinancialYear,
        "N/A",
      ),
    },
    {
      _id: `${idPrefix}-fatal`,
      incident: "No. of fatalities",
      current: safeText(block?.numberOfFatalities?.currentFinancialYear, "N/A"),
      previous: safeText(
        block?.numberOfFatalities?.previousFinancialYear,
        "N/A",
      ),
    },
    {
      _id: `${idPrefix}-hci`,
      incident: "High consequence injuries",
      current: safeText(
        block?.highConsequenceInjuries?.currentFinancialYear,
        "N/A",
      ),
      previous: safeText(
        block?.highConsequenceInjuries?.previousFinancialYear,
        "N/A",
      ),
    },
  ];

  const rehabCurrentRows = [
    {
      _id: "p3-l-cur-e",
      category: "Employees",
      total: safeText(
        leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear
          ?.employee?.total,
        "N/A",
      ),
      rehabilitated: safeText(
        leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear
          ?.employee?.rehabiliatated,
        "N/A",
      ),
    },
    {
      _id: "p3-l-cur-w",
      category: "Workers",
      total: safeText(
        leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear
          ?.workers?.total,
        "N/A",
      ),
      rehabilitated: safeText(
        leadership?.rehabilitatedEmploymentDetails?.currentFinancialYear
          ?.workers?.rehabiliatated,
        "N/A",
      ),
    },
  ];

  const rehabPreviousRows = [
    {
      _id: "p3-l-prev-e",
      category: "Employees",
      total: safeText(
        leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear
          ?.employee?.total,
        "N/A",
      ),
      rehabilitated: safeText(
        leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear
          ?.employee?.rehabiliatated,
        "N/A",
      ),
    },
    {
      _id: "p3-l-prev-w",
      category: "Workers",
      total: safeText(
        leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear
          ?.workers?.total,
        "N/A",
      ),
      rehabilitated: safeText(
        leadership?.rehabilitatedEmploymentDetails?.previousFinancialYear
          ?.workers?.rehabiliatated,
        "N/A",
      ),
    },
  ];

  return (
    <div className={styles.principleBlock}>
      <div
        className={styles.principleHeading}
      >{`PRINCIPLE 3: ${principleTitles[2]}`}</div>
      {loading ? (
        <div className={styles.meta}>Loading principle 3...</div>
      ) : null}
      <div className={styles.band}>I. ESSENTIAL INDICATOR</div>
      <div className={`${styles.question} ${styles.boldAnswer}`}>
        1. a. Details of measures for the well-being of employees:
      </div>
      <div className={styles.answer}>
        <div className={styles.subTitle} style={{ fontWeight: 400 }}>
          Total no. male Employees
        </div>
        <ul className={styles.bulletList}>
          <li>
            Permanent Employees:{" "}
            <span className={styles.boldAnswer}>
              {employeeTotals.malePermanent}
            </span>
          </li>
          <li>
            Other than permanent Employees:{" "}
            <span className={styles.boldAnswer}>
              {employeeTotals.maleOther}
            </span>
          </li>
        </ul>
        <div className={styles.subTitle} style={{ fontWeight: 400 }}>
          Total no. female Employees
        </div>
        <ul className={styles.bulletList}>
          <li>
            Permanent Employees:{" "}
            <span className={styles.boldAnswer}>
              {employeeTotals.femalePermanent}
            </span>
          </li>
          <li>
            Other than permanent Employees:{" "}
            <span className={styles.boldAnswer}>
              {employeeTotals.femaleOther}
            </span>
          </li>
        </ul>
      </div>
      <div className={styles.subTitle}>Permanent Employees</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "coveredBy", label: "% of Employees covered by" },
          { field: "maleNum", label: "Male(Num)" },
          { field: "malePct", label: "Male(%)" },
          { field: "femaleNum", label: "Female(Num)" },
          { field: "femalePct", label: "Female(%)" },
        ]}
        rows={employeePermanentBenefitRows}
      />
      <div className={styles.subTitle}>Other than permanent employees</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "coveredBy", label: "% of Employees covered by" },
          { field: "maleNum", label: "Male(Num)" },
          { field: "malePct", label: "Male(%)" },
          { field: "femaleNum", label: "Female(Num)" },
          { field: "femalePct", label: "Female(%)" },
        ]}
        rows={employeeOtherBenefitRows}
      />

      <div className={`${styles.question} ${styles.boldAnswer}`}>
        1. b. Details of measures for the well-being of workers:
      </div>
      <div className={styles.answer}>
        <div className={styles.subTitle} style={{ fontWeight: 400 }}>
          Total no. male Workers
        </div>
        <ul className={styles.bulletList}>
          <li>
            Permanent Workers:{" "}
            <span className={styles.boldAnswer}>
              {workerTotals.malePermanent}
            </span>
          </li>
          <li>
            Other than permanent Workers:{" "}
            <span className={styles.boldAnswer}>{workerTotals.maleOther}</span>
          </li>
        </ul>
        <div className={styles.subTitle} style={{ fontWeight: 400 }}>
          Total no. female Workers
        </div>
        <ul className={styles.bulletList}>
          <li>
            Permanent Workers:{" "}
            <span className={styles.boldAnswer}>
              {workerTotals.femalePermanent}
            </span>
          </li>
          <li>
            Other than permanent Workers:{" "}
            <span className={styles.boldAnswer}>
              {workerTotals.femaleOther}
            </span>
          </li>
        </ul>
      </div>
      <div className={styles.subTitle}>Permanent Workers</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "coveredBy", label: "% of Workers covered by" },
          { field: "maleNum", label: "Male(Num)" },
          { field: "malePct", label: "Male(%)" },
          { field: "femaleNum", label: "Female(Num)" },
          { field: "femalePct", label: "Female(%)" },
        ]}
        rows={workerPermanentBenefitRows}
      />
      <div className={styles.subTitle}>Other than permanent workers</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "coveredBy", label: "% of Workers covered by" },
          { field: "maleNum", label: "Male(Num)" },
          { field: "malePct", label: "Male(%)" },
          { field: "femaleNum", label: "Female(Num)" },
          { field: "femalePct", label: "Female(%)" },
        ]}
        rows={workerOtherBenefitRows}
      />
      {console.log(essential, "essential")}
      <div className={styles.question}>
        1. c. Spending on measures towards well-being of employees and workers
        (including permanent and other than permanent) in the following format:
      </div>
      <div className={styles.subTitle} style={{ fontWeight: 400 }}>
        Cost incurred on wellbeing measures as a % of total revenue of the
        company
      </div>
      <div className={styles.answer}>
        <ul className={styles.bulletList}>
          <li>
            Current Financial Year:{" "}
            <span className={styles.boldAnswer}>
              {essential.wellBeingMeasuresCurrent}
            </span>
          </li>
          <li>
            Previous Financial Year:{" "}
            <span className={styles.boldAnswer}>
              {essential.wellBeingMeasuresPrevious}
            </span>
          </li>
        </ul>
      </div>

      <div className={styles.question}>
        2. Details of retirement Benefits, for Current FY and Previous Financial
        Year.
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        {fyLineA}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "benefit", label: "Benefits " },
          {
            field: "employees",
            label: "No. of employees covered as a % of total employees ",
          },
          {
            field: "workers",
            label: "No. of workers covered as a % of total workers",
          },
          {
            field: "deducted",
            label: "Deducted and deposited with the authority (Yes / No / NA)",
          },
        ]}
        rows={retirementRows.map((row) => ({
          _id: `${row._id}-cur`,
          benefit: row.benefit,
          employees: row.currentEmployees,
          workers: row.currentWorkers,
          deducted: row.currentDeducted,
        }))}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        {fyLineB}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "benefit", label: "Benefits " },
          {
            field: "employees",
            label: "No. of employees covered as a % of total employees ",
          },
          {
            field: "workers",
            label: "No. of workers covered as a % of total workers",
          },
          {
            field: "deducted",
            label: "Deducted and deposited with the authority (Yes / No / NA)",
          },
        ]}
        rows={retirementRows.map((row) => ({
          _id: `${row._id}-prev`,
          benefit: row.benefit,
          employees: row.previousEmployees,
          workers: row.previousWorkers,
          deducted: row.previousDeducted,
        }))}
      />
      <div className={`${styles.question} ${styles.boldAnswer}`}>
        3. Are the premises / offices of the entity accessible to differently
        abled employees and workers, as per the requirements of the Rights of
        Persons with Disabilities Act, 2016? If not, whether any steps are being
        taken by the entity in this regard.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.accessibilityUnderRPWD2016?.status)}
        {essential?.accessibilityUnderRPWD2016?.remark
          ? `, ${safeText(essential?.accessibilityUnderRPWD2016?.remark, "")}`
          : ""}
      </div>
      <div className={`${styles.question} ${styles.boldAnswer}`}>
        4. Do you have an equal opportunity policy as per the Rights of Persons
        with Disabilities Act, 2016? If so, provide a web-link to the policy.
      </div>
      <div className={styles.answer}>
        {renderYesNo(essential?.rightsOfPersonsDisability2016?.status)}
        {essential?.rightsOfPersonsDisability2016?.status === true &&
        essential?.rightsOfPersonsDisability2016?.webLink
          ? `, ${safeText(essential.rightsOfPersonsDisability2016.webLink, "")}`
          : ""}
      </div>
      <div className={`${styles.question} ${styles.boldAnswer}`}>
        5. Return to work and Retention rates of permanent employees and workers
        that took parental leave.
      </div>
      <ul className={styles.bulletList}>
        <li className={`${styles.answer} ${styles.boldAnswer}`}>
          Permanent Employees
        </li>
      </ul>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "gender", label: "Gender" },
          { field: "returnToWorkRate", label: "Return to work rate" },
          { field: "retentionRate", label: "Retention rate" },
        ]}
        rows={employeeReturnRetentionRows}
      />
      <ul className={styles.bulletList}>
        <li className={`${styles.answer} ${styles.boldAnswer}`}>
          Permanent Workers
        </li>
      </ul>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "gender", label: "Gender" },
          { field: "returnToWorkRate", label: "Return to work rate" },
          { field: "retentionRate", label: "Retention rate" },
        ]}
        rows={workerReturnRetentionRows}
      />
      <div className={styles.question}>
        6. Is there a mechanism available to receive and redress grievances for
        the following categories of employees and worker? If yes, give details
        of the mechanism in brief.
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "segment", label: "" },
          { field: "mechanism", label: "NIC Code" },
          {
            field: "details",
            label: "(If Yes, then give details of the mechanism in brief)",
          },
        ]}
        rows={grievanceRows}
      />

      <div className={styles.question}>
        7. Membership of employees and worker in association(s) or Unions
        recognized by the listed entity:
      </div>
      <div className={styles.subTitle}>
        {fyLineA}
      </div>
      <div className={styles.answer}>Permanent Employees</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "total",
            label: "Total employee/workers in respective category",
          },
          {
            field: "inUnion",
            label:
              "No. of employees / workers in respective category, who are part of association(s) or Union",
          },
          { field: "percentage", label: "%" },
        ]}
        rows={membershipRowsFor(
          essential?.membershipInAssociationsOrUnions?.permanentEmployees
            ?.currentFinancialYear,
          "p3-e7-ce",
        )}
      />
      <div className={styles.answer}>Permanent Workers</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "total",
            label: "Total employee/workers in respective category",
          },
          {
            field: "inUnion",
            label:
              "No. of employees / workers in respective category, who are part of association(s) or Union",
          },
          { field: "percentage", label: "%" },
        ]}
        rows={membershipRowsFor(
          essential?.membershipInAssociationsOrUnions?.permanentWorkers
            ?.currentFinancialYear,
          "p3-e7-cw",
        )}
      />
      <div className={styles.subTitle}>
        {fyLineB}
      </div>
      <div className={styles.answer}>Permanent Employees</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "total",
            label: "Total employee/workers in respective category",
          },
          {
            field: "inUnion",
            label:
              "No. of employees / workers in respective category, who are part of association(s) or Union",
          },
          { field: "percentage", label: "%" },
        ]}
        rows={membershipRowsFor(
          essential?.membershipInAssociationsOrUnions?.permanentEmployees
            ?.previousFinancialYear,
          "p3-e7-pe",
        )}
      />
      <div className={styles.answer}>Permanent Workers</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "total",
            label: "Total employee/workers in respective category",
          },
          {
            field: "inUnion",
            label:
              "No. of employees / workers in respective category, who are part of association(s) or Union",
          },
          { field: "percentage", label: "%" },
        ]}
        rows={membershipRowsFor(
          essential?.membershipInAssociationsOrUnions?.permanentWorkers
            ?.previousFinancialYear,
          "p3-e7-pw",
        )}
      />

      <div className={styles.question}>
        8.Details of training given to employees and workers:
      </div>
      <div className={styles.subTitle}>
        {fyLineA}
      </div>
      <div className={styles.answer}>Employees</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total" },
          {
            label: "On Health and safety measures",
            children: [
              { field: "hsNumber", label: "Number" },
              { field: "hsPercent", label: "Percentage" },
            ],
          },
          {
            label: "On skill upgradation",
            children: [
              { field: "skillNumber", label: "Number" },
              { field: "skillPercent", label: "Percent" },
            ],
          },
        ]}
        rows={trainingRowsFor(
          essential?.trainingDetails?.currentFinancialYear
            ?.employeeTrainingUpgradation,
          "p3-e8-ce",
        )}
      />
      <div className={styles.answer}>Workers</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total" },
          {
            label: "On Health and safety measures",
            children: [
              { field: "hsNumber", label: "Number" },
              { field: "hsPercent", label: "Percentage" },
            ],
          },
          {
            label: "On skill upgradation",
            children: [
              { field: "skillNumber", label: "Number" },
              { field: "skillPercent", label: "Percent" },
            ],
          },
        ]}
        rows={trainingRowsFor(
          essential?.trainingDetails?.currentFinancialYear
            ?.workerTrainingUpgradation,
          "p3-e8-cw",
        )}
      />
      <div className={styles.subTitle}>
        {fyLineB}
      </div>
      <div className={styles.answer}>Employees</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total" },
          {
            label: "On Health and safety measures",
            children: [
              { field: "hsNumber", label: "Number" },
              { field: "hsPercent", label: "Percentage" },
            ],
          },
          {
            label: "On skill upgradation",
            children: [
              { field: "skillNumber", label: "Number" },
              { field: "skillPercent", label: "Percent" },
            ],
          },
        ]}
        rows={trainingRowsFor(
          essential?.trainingDetails?.previousFinancialYear
            ?.employeeTrainingUpgradation,
          "p3-e8-pe",
        )}
      />
      <div className={styles.answer}>Workers</div>
      <MultiHeaderReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total" },
          {
            label: "On Health and safety measures",
            children: [
              { field: "hsNumber", label: "Number" },
              { field: "hsPercent", label: "Percentage" },
            ],
          },
          {
            label: "On skill upgradation",
            children: [
              { field: "skillNumber", label: "Number" },
              { field: "skillPercent", label: "Percent" },
            ],
          },
        ]}
        rows={trainingRowsFor(
          essential?.trainingDetails?.previousFinancialYear
            ?.workerTrainingUpgradation,
          "p3-e8-pw",
        )}
      />

      <div className={styles.question}>
        9. Details of performance and career development reviews of employees
        and worker:
      </div>
      <div className={styles.answer}>a. Employees</div>
      <div className={styles.subTitle}>
        {fyInCurrent}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total No. of Employees" },
          { field: "reviewed", label: "No. of Employees" },
          { field: "percentage", label: "% of Employees" },
        ]}
        rows={performanceRowsFor(
          essential?.performanceAndCareerReviews?.employee
            ?.currentFinancialYear,
          "p3-e9-ec",
        )}
      />
      <div className={styles.subTitle}>
        {fyInPrevious}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total No. of Employees" },
          { field: "reviewed", label: "No. of Employees" },
          { field: "percentage", label: "% of Employees" },
        ]}
        rows={performanceRowsFor(
          essential?.performanceAndCareerReviews?.employee
            ?.previousFinancialYear,
          "p3-e9-ep",
        )}
      />

      <div className={styles.answer}>b. Workers</div>
      <div className={styles.subTitle}>
        {fyInCurrent}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total No. of Workers" },
          { field: "reviewed", label: "No. of Workers" },
          { field: "percentage", label: "% of Workers" },
        ]}
        rows={performanceRowsFor(
          essential?.performanceAndCareerReviews?.workers?.currentFinancialYear,
          "p3-e9-wc",
        )}
      />
      <div className={styles.subTitle}>
        {fyInPrevious}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total No. of Workers" },
          { field: "reviewed", label: "No. of Workers" },
          { field: "percentage", label: "% of Workers" },
        ]}
        rows={performanceRowsFor(
          essential?.performanceAndCareerReviews?.workers
            ?.previousFinancialYear,
          "p3-e9-wp",
        )}
      />

      <div className={styles.question}>
        10. Health and safety management system:
      </div>
      <div
        className={styles.answer}
      >{`a. Whether an occupational health and safety management system has been implemented by the entity? (Yes/ No). If yes, the coverage such system?: ${renderYesNo(
        essential?.healthAndSafetyManagement?.occupationalHealthAndSafety
          ?.status,
      )}${
        essential?.healthAndSafetyManagement?.occupationalHealthAndSafety
          ?.status
          ? `, ${safeText(essential?.healthAndSafetyManagement?.occupationalHealthAndSafety?.remark, "N/A")}`
          : ""
      }
b. What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?: ${safeText(
        essential?.healthAndSafetyManagement?.workRelatedHazardAssessment
          ?.remark,
        "N/A",
      )}
c. Whether you have processes for workers to report work related hazards and remove themselves from such risks. (Y/N)?: ${renderYesNo(
        essential?.healthAndSafetyManagement
          ?.hazardReportingAndSelfRemovalProcess?.status,
      )}
d. Do the employees/ workers of the entity have access to non-occupational medical and healthcare services? (Y/N)?: ${renderYesNo(
        essential?.healthAndSafetyManagement
          ?.nonOccupationalMedicalServicesAccess?.status,
      )}`}</div>

      <div className={styles.question}>
        11. Details of safety related incidents, in the following format:
      </div>
      <div className={styles.answer}>a.Employees</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "incident", label: "Safety Incident/Number" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={safetyIncidentRowsFor(
          essential?.safetyIncidents?.employees,
          "p3-e11-e",
        )}
      />
      <div className={styles.answer}>b.Workers</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "incident", label: "Safety Incident/Number" },
          { field: "current", label: fyCurrentColLabel },
          { field: "previous", label: fyPreviousColLabel },
        ]}
        rows={safetyIncidentRowsFor(
          essential?.safetyIncidents?.workers,
          "p3-e11-w",
        )}
      />

      <div className={styles.question}>
        12. Describe the measures taken by the entity to ensure a safe and
        healthy work place.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.safetyMeasures, "N/A")}
      </div>

      <div className={styles.question}>
        13. Number of Complaints on the following made by employees and workers:
      </div>
      <div className={styles.subTitle}>
        {fyLineA}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "filled", label: "Filed during the year" },
          { field: "pending", label: "Pending resolution at the end of year" },
          { field: "remark", label: "Remarks" },
        ]}
        rows={[
          {
            _id: "p3-e13-cur-wc",
            category: "Working Conditions",
            filled: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.workingCondition?.filled,
              "N/A",
            ),
            pending: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.workingCondition?.pending,
              "N/A",
            ),
            remark: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.workingCondition?.remark,
              "N/A",
            ),
          },
          {
            _id: "p3-e13-cur-hs",
            category: "Health & Safety",
            filled: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.healthAndSafety?.filled,
              "N/A",
            ),
            pending: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.healthAndSafety?.pending,
              "N/A",
            ),
            remark: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.currentFinancialYear?.healthAndSafety?.remark,
              "N/A",
            ),
          },
        ]}
      />
      <div className={styles.subTitle}>
        {fyLineB}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "filled", label: "Filed during the year" },
          { field: "pending", label: "Pending resolution at the end of year" },
          { field: "remark", label: "Remarks" },
        ]}
        rows={[
          {
            _id: "p3-e13-prev-wc",
            category: "Working Conditions",
            filled: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.workingCondition?.filled,
              "N/A",
            ),
            pending: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.workingCondition?.pending,
              "N/A",
            ),
            remark: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.workingCondition?.remark,
              "N/A",
            ),
          },
          {
            _id: "p3-e13-prev-hs",
            category: "Health & Safety",
            filled: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.healthAndSafety?.filled,
              "N/A",
            ),
            pending: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.healthAndSafety?.pending,
              "N/A",
            ),
            remark: safeText(
              essential?.complaintsMadeByEmployeesAndWorkers
                ?.previousFinancialYear?.healthAndSafety?.remark,
              "N/A",
            ),
          },
        ]}
      />

      <div className={styles.question}>14. Assessments for the year:</div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "assessed",
            label:
              "% of your plants and offices that were assessed (by entity or statutory authorities or third parties)",
          },
        ]}
        rows={[
          {
            _id: "p3-e14-hs",
            category: "Health and safety practices",
            assessed: safeText(
              essential?.assessmentYear?.healthAndSafetyPractice,
              "N/A",
            ),
          },
          {
            _id: "p3-e14-wc",
            category: "Working conditions",
            assessed: safeText(
              essential?.assessmentYear?.workingCondition,
              "N/A",
            ),
          },
        ]}
      />

      <div className={styles.question}>
        15.Provide details of any corrective action taken or underway to address
        safety-related incidents (if any) and on significant risks / concerns
        arising from assessments of health & safety practices and working
        conditions.
      </div>
      <div className={styles.answer}>
        {safeText(essential?.healthAndSafetyPracticesWorking, "N/A")}
      </div>

      <div className={styles.band}>II. LEADERSHIP INDICATOR</div>
      <div className={styles.question}>
        1. Does the entity extend any life insurance or any compensatory package
        in the event of death of (A) Employees (Y/N) (B) Workers (Y/N)?
      </div>
      <div className={styles.answer}>
        {`Employees: ${safeText(leadership?.lifeInsuranceOrCompensatoryPackage?.employee, "N/A")}\nWorkers: ${safeText(
          leadership?.lifeInsuranceOrCompensatoryPackage?.workers,
          "N/A",
        )}`}
      </div>
      <div className={styles.question}>
        2. Provide the measures undertaken by the entity to ensure that
        statutory dues have been deducted and deposited by the value chain
        partners.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.statutoryDuesDeductedDeposited, "N/A")}
      </div>
      <div className={styles.question}>
        3. Provide the number of employees / workers having suffered high
        consequence work-related injury / ill-health / fatalities (as reported
        in Q11 of Essential Indicators above), who have been rehabilitated and
        placed in suitable employment or whose family members have been placed
        in suitable employment:
      </div>
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        {fyLineA}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total no. of affected employees/workers" },
          {
            field: "rehabilitated",
            label:
              "No. of employees/workers that are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment",
          },
        ]}
        rows={rehabCurrentRows}
      />
      <div className={`${styles.answer} ${styles.boldAnswer}`}>
        {fyLineB}
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          { field: "total", label: "Total no. of affected employees/workers" },
          {
            field: "rehabilitated",
            label:
              "No. of employees/workers that are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment",
          },
        ]}
        rows={rehabPreviousRows}
      />
      <div className={styles.question}>
        4. Does the entity provide transition assistance programs to facilitate
        continued employability and the management of career endings resulting
        from retirement or termination of employment?
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.employabilityandmanagement, "N/A")}
      </div>
      <div className={styles.question}>
        5. Details on assessment of value chain partners:
      </div>
      <ReportTable
        showSerialNumber={false}
        columns={[
          { field: "category", label: "Category" },
          {
            field: "assessed",
            label:
              "% of value chain partners (by value of business done with such partners) that were assessed",
          },
        ]}
        rows={[
          {
            _id: "p3-l5-1",
            category: "Health and safety practices",
            assessed: safeText(
              pickFirst(
                leadership?.assessmentOfValueChainPartners
                  ?.healthAndSafetyPractice,
                leadership?.assessmentYear?.healthAndSafetyPractice,
              ),
              "N/A",
            ),
          },
          {
            _id: "p3-l5-2",
            category: "Working Conditions",
            assessed: safeText(
              pickFirst(
                leadership?.assessmentOfValueChainPartners?.workingCondition,
                leadership?.assessmentYear?.workingCondition,
              ),
              "N/A",
            ),
          },
        ]}
      />
      <div className={styles.question}>
        6. Provide details of any corrective actions taken or underway to
        address significant risks / concerns arising from assessments of health
        and safety practices and working conditions of value chain partners.
      </div>
      <div className={styles.answer}>
        {safeText(leadership?.valuechainpartners, "N/A")}
      </div>
      <div className={styles.pageBreak} />
    </div>
  );
};

export default Principle3;
